import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "@/lib/api";
import { Modal } from "@/components/Modal";

/** The staff fields the form edits; the list row and the detail payload both satisfy it. */
export interface StaffFormTarget {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  startDate: string;
  email?: string | null;
  phone?: string | null;
  description?: string | null;
  assignedBuildings?: string[];
}

const emptyForm = () => ({
  firstName: "",
  lastName: "",
  position: "",
  startDate: new Date().toISOString().slice(0, 10),
  email: "",
  phone: "",
  description: "",
  assignedBuildings: [] as string[],
});

/**
 * Create (no `target`) or edit (`target` given) a staff member. One form for both so a
 * typo in a name, position, phone or email can be corrected in place — the API has
 * always allowed it (PUT /staff/:id); only the pencil was missing.
 */
export function StaffFormModal({
  open,
  target,
  onClose,
  onDone,
}: {
  open: boolean;
  target?: StaffFormTarget | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(
      target
        ? {
            firstName: target.firstName,
            lastName: target.lastName,
            position: target.position,
            startDate: target.startDate.slice(0, 10),
            email: target.email ?? "",
            phone: target.phone ?? "",
            description: target.description ?? "",
            assignedBuildings: target.assignedBuildings ?? [],
          }
        : emptyForm()
    );
  }, [open, target]);

  // The buildings to choose from are the ones the unit catalog uses (E–R today).
  const { data: units = [] } = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api.get<{ building: string | null }[]>("/units")).data,
    enabled: open,
  });
  const buildings = [
    ...new Set([...units.map((u) => u.building?.trim().toUpperCase()).filter((b): b is string => !!b), ...form.assignedBuildings]),
  ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const toggleBuilding = (b: string) =>
    setForm((f) => ({
      ...f,
      assignedBuildings: f.assignedBuildings.includes(b) ? f.assignedBuildings.filter((x) => x !== b) : [...f.assignedBuildings, b],
    }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = { ...form, email: form.email || null, phone: form.phone || null, description: form.description.trim() || null };
      return target ? (await api.put(`/staff/${target.id}`, body)).data : (await api.post("/staff", body)).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (err) => {
      setError((err as AxiosError<{ message: string }>).response?.data?.message ?? "Error");
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={target ? t("hr.editStaff") : t("hr.newStaff")}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("hr.firstName")}</label>
            <input className="input" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("hr.lastName")}</label>
            <input className="input" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">{t("hr.position")}</label>
          <input className="input" required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("hr.startDate")}</label>
            <input type="date" className="input" required value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("hr.phone")}</label>
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">{t("hr.email")}</label>
          <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("hr.description")}</label>
          <textarea
            className="input min-h-[90px]"
            value={form.description}
            placeholder={t("hr.descriptionPlaceholder")}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div>
          <label className="label">{t("hr.assignedBuildings")}</label>
          <div className="flex flex-wrap gap-1.5">
            {buildings.map((b) => {
              const on = form.assignedBuildings.includes(b);
              return (
                <button
                  key={b}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleBuilding(b)}
                  className={
                    on
                      ? "min-w-[2.25rem] px-2 py-1 text-sm rounded border border-primary-700 bg-primary-700 text-white"
                      : "min-w-[2.25rem] px-2 py-1 text-sm rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                  }
                >
                  {b}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-1">{t("hr.assignedBuildingsHint")}</p>
        </div>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>
            {t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
